<script setup lang="ts">
import { useSourceStore } from '@/stores/SourceStore.ts';
import { onMounted, onUnmounted, reactive } from 'vue';
import { requestInputSourcesAction } from '@/actions/electron/RequestInputSourcesAction.ts';
import type { InputSource } from '@/types/InputSource.ts';
import { setInputSourceAction } from '@/actions/app/SetInputSourceAction.ts';
import {
    DropdownMenuCheckboxItem,
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import Loader from '@/components/loader/Loader.vue';
import { isInputSourceAutoDetected } from '@/utils/AutoDetectUtils.ts';
import { MonitorIcon } from 'lucide-vue-next';
import { eventEmitter } from '@/lib/eventEmitter.ts';
import { useSettingsStore } from '@/stores/SettingsStore.ts';

const hostStore = useSourceStore();
const settingsStore = useSettingsStore();

interface ComponentState {
    screenPermissionFetched: boolean;
    hasMissingScreenPermission: boolean;
}

const state: ComponentState = reactive({
    screenPermissionFetched: false,
    hasMissingScreenPermission: false,
});

const getSources = () => {
    requestInputSourcesAction();

    if (!state.screenPermissionFetched) {
        window.electronApi.requestPermissionStatus();
        state.screenPermissionFetched = true;
    }
};

const setInputSource = (source: InputSource) => {
    setInputSourceAction(source);
};

const openPermissionWindow = () => {
    window.electronApi.openPermissionWindow();
};

const onRequestPermissionStatusResponse = (status: { screen: boolean }) => {
    if (!status.screen) {
        state.hasMissingScreenPermission = true;
    }
};

onMounted(async () => {
    eventEmitter.addListener(
        'onRequestPermissionStatusResponse',
        onRequestPermissionStatusResponse,
    );

    if (!hostStore.sources.length) {
        getSources();
    }
});

onUnmounted(() => {
    eventEmitter.removeListener(
        'onRequestPermissionStatusResponse',
        onRequestPermissionStatusResponse,
    );
});
</script>

<template>
    <DropdownMenuSub>
        <DropdownMenuSubTrigger
            class="flex items-center gap-2"
            :disabled="settingsStore.isOnboarding"
        >
            <MonitorIcon class="size-4" />
            <span>Input source</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
            <DropdownMenuSubContent class="p-0">
                <div class="max-h-[65vh] overflow-y-scroll p-1">
                    <DropdownMenuItem
                        v-if="state.hasMissingScreenPermission"
                        @click="openPermissionWindow"
                        @select="(event: Event) => event.preventDefault()"
                    >
                        <div class="w-60 text-center text-red-700">
                            Leetcode Wizard needs permission to view your screen, click here to open
                            your permission settings.
                        </div>
                    </DropdownMenuItem>
                    <DropdownMenuCheckboxItem
                        v-for="source in hostStore.sources"
                        v-else
                        :key="source.id"
                        :checked="source.id === hostStore.selectedSourceId"
                        :disabled="hostStore.sourcesLoading"
                        @click="setInputSource(source)"
                        @select="(event: Event) => event.preventDefault()"
                        :class="{
                            'border border-green-500': isInputSourceAutoDetected(source.name),
                        }"
                    >
                        <img
                            :src="source.thumbnail"
                            alt="Thumbnail"
                            class="me-2 h-12 w-20 rounded"
                        />
                        <span class="max-w-48 truncate">
                            {{ source.name }}
                        </span>
                    </DropdownMenuCheckboxItem>
                </div>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                    class="flex items-center justify-center"
                    :disabled="hostStore.sourcesLoading"
                    @click="getSources"
                    @select="(event: Event) => event.preventDefault()"
                >
                    <span v-if="!hostStore.sourcesLoading"> Refresh </span>
                    <Loader v-else :width="18" :height="18" />
                </DropdownMenuItem>
            </DropdownMenuSubContent>
        </DropdownMenuPortal>
    </DropdownMenuSub>
</template>
